import { useLazyQuery, useMutation } from "@apollo/client";
import {
  EyeSlashIcon,
  HandThumbDownIcon,
  HandThumbUpIcon,
  UserIcon,
} from "@heroicons/react/24/solid";
import { Button } from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ActionButton from "../../components/ActionButton/ActionButton";
import EmptyState from "../../components/EmptyState/EmptyState";
import { ErrorAlert } from "../../components/ErrorAlert/ErrorAlert";
import Loader from "../../components/Loader/Loader";
import Page from "../../components/Page/Page";
import Badge from "../../components/Styled/Badge";
import { Row } from "../../components/Styled/Container";
import {
  MediaActionRow,
  MediaActions,
  MediaCard,
  MediaCornerActions,
  MediaFrame,
  MediaGrid,
  MediaImage,
} from "../../components/Styled/Media";
import { PagingResult, Photo } from "../../graphql/graphql";
import { APPROVE_PHOTO } from "../../graphql/mutations-gql";
import { PRIVATE_PHOTOS } from "../../graphql/queries-gql";

const PAGE_SIZE = 20;

const PrivatePhotos = () => {
  const [fetchPhotos, { loading, error }] = useLazyQuery(PRIVATE_PHOTOS);
  const [page, setPage] = useState<PagingResult | undefined>(undefined);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [approvePhoto] = useMutation(APPROVE_PHOTO);
  const navigate = useNavigate();

  useEffect(() => {
    if (!error && !page) {
      fetchPhotos({
        variables: {
          paging: {
            type: "page",
            pageArgs: {
              page: 1,
              limit: PAGE_SIZE,
            },
          },
        },
        onCompleted: (data) => {
          console.log("fetchPhotos onCompleted", data);
          setPage(data.privatePhotos.paging);
          setPhotos(data.privatePhotos.nodes);
        },
        onError: (error) => {
          console.log("fetchPhotos onError", error);
        },
      });
    }
  }, [error, fetchPhotos, page]);

  const showImage = (src: string) => {
    window.open(src, "_blank");
  };

  const showMore = () => {
    fetchPhotos({
      variables: {
        paging: {
          type: "page",
          pageArgs: {
            page: page?.nextPage,
            limit: PAGE_SIZE,
          },
        },
      },
      onCompleted: (data) => {
        console.log("fetchPhotos onCompleted", data);
        setPage(data.privatePhotos.paging);
        setPhotos([...photos, ...data.privatePhotos.nodes]);
      },
      onError: (error) => {
        console.log("fetchPhotos onError", error);
      },
    });
  };

  const handleApprove = (value: boolean, uploadId: string) => {
    approvePhoto({
      variables: {
        uploadId,
        value,
      },
      onCompleted: (data) => {
        // Completion means we should remove the photo.
        const newPhotos = photos.filter((photo) => photo.uploadId !== uploadId);
        setPhotos(newPhotos);
      },
      onError: (error) => {
        console.log("approvePhoto onError", error);
      },
    });
  };

  return (
    <Page
      title="Private photos"
      description="Photos users share privately. Hover a photo to approve or reject it."
      badge={
        page?.total != null && <Badge>{page.total} total</Badge>
      }
    >
      {error && <ErrorAlert error={error} />}

      {page && !error && photos.length === 0 && (
        <EmptyState
          Icon={EyeSlashIcon}
          title="No private photos"
          description="Nothing to review right now."
        />
      )}

      {photos && photos.length > 0 && (
        <MediaGrid $min={170} $gap={12}>
          {photos.map((photo) => (
            <MediaCard key={photo.uploadId}>
              <MediaFrame $ratio="1 / 1" onClick={() => showImage(photo.url)}>
                <MediaImage src={photo.thumbnailUrl} alt="" loading="lazy" />
                <MediaCornerActions>
                  <ActionButton
                    label="Open user"
                    size={30}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/user/${photo.uploadById}`);
                    }}
                  >
                    <UserIcon />
                  </ActionButton>
                </MediaCornerActions>
                <MediaActions>
                  <MediaActionRow>
                    <ActionButton
                      label="Approve"
                      tone="approve"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleApprove(true, photo.uploadId);
                      }}
                    >
                      <HandThumbUpIcon />
                    </ActionButton>
                    <ActionButton
                      label="Reject"
                      tone="reject"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleApprove(false, photo.uploadId);
                      }}
                    >
                      <HandThumbDownIcon />
                    </ActionButton>
                  </MediaActionRow>
                </MediaActions>
              </MediaFrame>
            </MediaCard>
          ))}
        </MediaGrid>
      )}

      {loading && (
        <Row justifyContent="center">
          <Loader />
        </Row>
      )}

      {page?.hasNextPage && !loading && (
        <Row justifyContent="center">
          <Button variant="outlined" color="inherit" onClick={() => showMore()}>
            Show more photos
          </Button>
        </Row>
      )}
    </Page>
  );
};
export default PrivatePhotos;
